import axios from "axios";
import { Cookies } from "react-cookie";

import { StaticValue } from "../seed/constants";
import {
	clearAuthorizationToken,
	getAuthorizationToken,
	isClaimAuthorizationToken,
	isUserAuthorizationExpired,
	isUserAuthorizationToken,
	subscribeToAuthorization,
} from "./auth/authToken";
import { clearClaimSession, getClaimToken } from "./companies/claimSession";

const staticValue = new StaticValue();
const staticValues = staticValue.getDefaultValues();

const cookies = new Cookies();

function getUsableAuthorizationToken(token) {
	if (typeof token !== "string" || !token || isClaimAuthorizationToken(token)) return null;
	if (isUserAuthorizationToken(token) && isUserAuthorizationExpired(token)) return null;
	return token;
}

function applyRequestAuthorization(config) {
	const configuredAuthToken = getUsableAuthorizationToken(config.headers?.Authorization);
	const authToken = getUsableAuthorizationToken(getAuthorizationToken());
	const claimToken = getClaimToken();

	if (config.authMode === "claim") {
		delete config.headers.Authorization;
		if (claimToken) config.headers.Authorization = claimToken;
	} else if (configuredAuthToken) {
		// A token supplied directly on this request, or synchronously applied to the axios client,
		// is authoritative. Do not delete it merely because the cookie has not become readable yet.
		config.headers.Authorization = configuredAuthToken;
	} else if (authToken && authToken !== claimToken) {
		config.headers.Authorization = authToken;
	} else {
		delete config.headers.Authorization;
	}

	return config;
}

export const API = axios.create({
	baseURL: staticValues.apiUrl,
	headers: {
		"Content-Type": "application/json",
		...staticValues.commonHeader,
	},
});

API.interceptors.request.use(applyRequestAuthorization);

/**
 * Point both axios layers at a token the moment a provider issues one.
 *
 * The auth store remains the source of truth and the cookie persists it for the next
 * page load. Inline sign-in flows issue protected requests before another render, so
 * both long-lived clients must still be mutated synchronously.
 */
export function applyAuthorizationHeader(token) {
	// ResumeDataService configures global axios lazily. Do that before applying the token so its
	// one-time defaults initialization cannot wipe this header on the next client-side route.
	configureLegacyGlobalAxios();

	// Mutate the shared API instance and the legacy global axios singleton.
	if (token) {
		API.defaults.headers.Authorization = token;
		API.defaults.headers.common.Authorization = token;
		axios.defaults.headers.common.Authorization = token;
		return;
	}

	delete API.defaults.headers.Authorization;
	delete API.defaults.headers.common.Authorization;
	delete axios.defaults.headers.common.Authorization;
}

/**
 * One shared auth-failure policy for BOTH axios clients (the shared `API`
 * instance and the legacy global `axios` still used by ResumeDataService).
 *
 * - 401: the credential could not be resolved to a user — clear it and send the
 *   visitor to /signin, preserving where they were.
 * - 403: an authenticated permission/ownership failure (or an unsupported PATCH
 *   section). This is NOT an authentication failure — surface it to the caller,
 *   never clear the session. (The backend returns 401, not 403, for
 *   missing/expired credentials.)
 * - 402: payment required — raised by the create / copy / template-change flows.
 *
 * The navigation is guarded so concurrent failures across both clients start at
 * most one redirect.
 */
let authRedirectStarted = false;

function beginAuthRedirect(destination) {
	if (authRedirectStarted) return;
	authRedirectStarted = true;
	window.location.href = destination;
}

export function onAuthFailure(error) {
	const status = error?.response?.status;

	if (status === 402) {
		beginAuthRedirect("/payments");
		return;
	}

	// Only 401 means "not authenticated". 403 is a permission error for an
	// authenticated user and must not log them out.
	if (status !== 401) return;

	if (error.config?.authFailureMode === "preserve") return;

	// A request started immediately before a login/session switch can finish after the new token is
	// active. Its 401 belongs to the old (or missing) credential and must not tear down the new one.
	const currentToken = getAuthorizationToken();
	const failedRequestToken = error.config?.headers?.Authorization;
	if (currentToken && failedRequestToken !== currentToken) return;

	const ref = encodeURIComponent(window.location.pathname + window.location.search + window.location.hash);

	if (error.config?.authMode === "claim") {
		applyAuthorizationHeader(null);
		clearClaimSession();
		beginAuthRedirect(`/signin?ref=${ref}`);
		return;
	}

	// An anonymous author's credential is the id of the throwaway user their
	// resume was created under. A 401 means that resume is gone, not that a
	// session expired — there is no account behind it, so /signin is a form they
	// can never satisfy. Drop the dead pointers instead and leave them where they
	// are: the builder then behaves like a first visit and their next step starts
	// a new resume.
	const token = currentToken;
	if (token && !isUserAuthorizationToken(token) && !isClaimAuthorizationToken(token)) {
		applyAuthorizationHeader(null);
		clearAuthorizationToken();
		cookies.remove(staticValues.localStorageResumeId, { path: "/" });
		return;
	}

	// A 401 describes this request, not necessarily the user's identity. Resource endpoints can use
	// it for a missing account-owned row, a stale object, or an endpoint-specific authorization
	// rule. Only the identity read opts into destroying the global user session. Without this gate,
	// one delayed ancillary response can make a valid header suddenly render as signed out.
	if (error.config?.authFailureMode !== "logout") return;

	applyAuthorizationHeader(null);
	clearAuthorizationToken();
	localStorage.removeItem("profileData");
	localStorage.removeItem("isDropzOpsUser");
	localStorage.removeItem("jobPreferences");
	localStorage.removeItem("isSubscribed");
	localStorage.removeItem("resumeList");
	beginAuthRedirect(`/signin?ref=${ref}`);
}

API.interceptors.response.use(
	(response) => response,
	(error) => {
		onAuthFailure(error);
		return Promise.reject(error);
	},
);

/**
 * Configure the legacy global `axios` instance that ResumeDataService still
 * issues through. Idempotent. Mirrors the `API` client: non-auth defaults from
 * commonHeader, Authorization applied per-request from the live singleton (including
 * a prefix-less anonymous resume credential), and the shared auth-failure policy.
 * Retire this once the last ResumeDataService caller moves to `API`.
 */
let legacyGlobalAxiosConfigured = false;

export function configureLegacyGlobalAxios() {
	if (legacyGlobalAxiosConfigured) return;
	legacyGlobalAxiosConfigured = true;

	axios.defaults.headers.common = { ...staticValues.commonHeader };

	axios.interceptors.request.use(applyRequestAuthorization);

	axios.interceptors.response.use(null, (error) => {
		onAuthFailure(error);
		return Promise.reject(error);
	});
}

function synchronizeAuthorizationHeaders() {
	applyAuthorizationHeader(getUsableAuthorizationToken(getAuthorizationToken()));
}

synchronizeAuthorizationHeaders();
subscribeToAuthorization(synchronizeAuthorizationHeaders);
